import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import * as React from "react";
import { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import AddChildInteractionButton from "../AddChildInteractionButton";
import { useRowSelector, useStoreSelector } from "../../../store/Hooks";
import Divider from "@mui/material/Divider";
import Interaction from "../../../models/interactions/Interaction";
import InteractionNode from "../../../models/interactions/InteractionNode";
import ConditionBar from "../../conditions/ConditionBar";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import InteractionConditionsButton from "../ui/InteractionConditionsButton";
import InteractionEditButton from "../ui/InteractionEditButton";
import "./pathway.css";
// import PathwayIcon from "./PathwayIcon";
import Stack from "@mui/material/Stack";
import InteractionPopoutButton from "../ui/InteractionPopoutButton";
import InteractionTypeIndicator from "../ui/interactionpropdisplay/typeindicators/InteractionTypeIndicator";
import PathwayIcon from "./pages/ui/icons/PathwayIcon";
import CommitButton from "../../commit/CommitButton";
import CircularProgress from "@mui/material/CircularProgress";
import InteractionDeleteButton from "../ui/InteractionDeleteButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TableRowsOutlinedIcon from "@mui/icons-material/TableRowsOutlined";
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import CopyAllIcon from '@mui/icons-material/CopyAll';
import { IconButton } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import IosShareIcon from "@mui/icons-material/IosShare";
import { useDispatch } from "react-redux";
import { navSliceActions } from "../../../store/slices/NavSlice";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import { useNavigate } from "react-router-dom";
import InteractionDisabledOverlay from "../ui/InteractionDisabledOverlay";
import InteractionCopyButton from "../ui/InteractionCopyButton";

// NOTE: THIS IS ONLY BEING USED FOR PATHWAYS IN NODES RIGHT NOW
interface PathwayProps {
  childInteractions: Array<any>;
  // childIntervalSets: Array<any>;
  interaction: Interaction;
  rootInteractionID: number;
  node: InteractionNode | null;
  isInitial: boolean;
  isReferenceSet: boolean;
  handleDeleteInteraction: any;
  dragPreview?: boolean | null | undefined;
}

const PathwayAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  ".MuiAccordionSummary-content": {
    display: "block",
    // Ensure the content takes the full width and allows flex item to reverse the order
    flexGrow: 1,
  },
  ".MuiAccordionSummary-content.Mui-expanded": {
    margin: "12px 0",
  },
  // Additional styles to reverse the order of the expand icon and summary content
  flexDirection: "row-reverse", // Reverse the order of flex items (icon comes first)
  "& .MuiAccordionSummary-expandIconWrapper": {
    // Adjust the margin if needed, for example to add space between the icon and the text
    marginLeft: theme.spacing(1),
    marginRight: 0, // Reset default marginRight to align the icon closely to the text
  },
  // You might also need to adjust alignment if the text and icon are misaligned vertically
  alignItems: "center",
}));

const Pathway = (props: PathwayProps) => {
  const previewMode = useStoreSelector((state) => state.nav.previewMode);
  const navHistory = useStoreSelector((state) => state.nav.navHistory);

  const [expanded, setExpanded] = useState<string | false>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const pathway = useRowSelector(Interaction, props.interaction.id);
  const pathwayNode = useRowSelector(InteractionNode, props.node?.id || 0);

  const navigate = useNavigate();


  // if set is shared, edit mode should be off (locked) by default
  // otherwise if its anonymous it should be unlocked and all controls available
  const [editMode, setEditMode] = useState<boolean>(pathway?.shared ? false : true);

  const dispatch = useDispatch();

  const backgroundColor: string =
    props.dragPreview === true ? "lightyellow" : "";

  const handleOpenIntervalSet = () => {
    setTimeout(() => {
      setIsLoading(false);
    }, 500);
  };

  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  let setID = props.rootInteractionID;
  let parentID = props.rootInteractionID;

  // get the set from from the node representing this one
  // (if it is "popped out")
  if (navHistory && navHistory.length > 0) {
    setID = navHistory[navHistory.length - 1].setid
  }

  // if a node is passed in, that should take precedence
  if (props.node) {
    if (props.node.set_interaction_id) {
      setID = props.node.set_interaction_id;
    }
    if (props.node.id) {
      parentID = props.node.child_interaction_id;
    }
  }
  // if there's only one child interaction,
  // its the initial insertion point
  // since we already have an initial button to handle this,
  // it can be removed until more interactions are added
  if (props.childInteractions.length === 1) {
    props.childInteractions.pop();
  }
  // if this pathway is the selected rootInteractionID,
  // its immediate children should be shown differently than
  // the descendants of those immediate children
  if (props.interaction.id === props.rootInteractionID) {
    return (
      <div style={{ margin: "10px", padding: "10px 60px" }}>
        {props.node && props.node.condition_id !== null && (
          <div>
            <ConditionBar
              sx={{
                // position: "absolute",
                // left: 0,
                // top: 0,
                height: "500px",
                width: "100%",
              }}
              table={InteractionNode}
              tableID={props.node.id}
            />
          </div>
        )}
        {/* check for length 1 because there will always be an insertionPoint span in childInteractions */}
        {/* {props.childInteractions.length <= 1 && !previewMode && (
          <AddChildInteractionButton
            setID={setID}
            parentID={parentID}
            type={props.interaction.properties.type}
            main={false}
            numChildren={props.childInteractions.length}
            intervalSetID={null}
          />
        )} */}
        <span style={{ float: "right" }}>
          <InteractionTypeIndicator
            type={props.interaction.properties.type}
            variant="outlined"
          />
        </span>
        <h2>
          {props.interaction.title}
          {!props.interaction.shared && (
            <Tooltip title={"Share Interaction"}>
              <IconButton
                data-testid="editIntervalSetButton"
                //disabled={props.interaction.shared}
                onClick={(e) => {
                  e.stopPropagation();
                  // open modal, ask user to confirm share action
                  dispatch(
                    navSliceActions.openModal({
                      caresenseModalAlertType: "interaction_share",
                      caresenseModalAlertMessage: "This action will permanently make this pathway available for use on other Interactions. Proceed?",
                      caresenseModalAlertEntityID: props.interaction.id,
                      modalCallback: () => {
                        pathway?.update("shared", true).save();
                        if (props.node && pathway) {
                          InteractionNode.updateInteractionSetID(props.node?.parent_interaction_id, props.node?.set_interaction_id, pathway?.id, pathway?.id)
                        }
                      } // post callback to redux so modal alert calls it on user approval
                    }),
                  );
                }}
              >
                <IosShareIcon />
              </IconButton>
            </Tooltip>
          )}
          {props.interaction.shared && <Tooltip title={"Unlock for editing"}>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setEditMode(!editMode);
              }}
            >
              {!editMode && <LockIcon style={{ color: "red" }} />}
              {editMode && <LockOpenIcon style={{ color: "#1976d2" }} />}
            </IconButton>
          </Tooltip>}
          {props.interaction.shared && <CommitButton setID={setID} parentID={parentID} shared={props.interaction.shared} />}
          {!previewMode && (
            <InteractionEditButton interaction={props.interaction} />
          )}
          {!previewMode && (
            <InteractionDeleteButton
              interaction={props.interaction}
              handler={props.handleDeleteInteraction}
            />
          )}
          {editMode && props.interaction.shared && (
            <span style={{ color: "red" }}>
              <h6 style={{ color: "red", marginTop: "5px" }}>
                <span style={{ marginTop: "-150px" }}>
                  <ErrorOutlineOutlinedIcon />
                </span>
                Editing shared content!
              </h6>
            </span>
          )}
        </h2>
        <Divider />
        {props.interaction.shared && <InteractionDisabledOverlay type="pathway_main" isVisible={!editMode} />}

        {/* check for length 1 because there will always be an insertionPoint span in childInteractions */}
        {props.childInteractions.length <= 1 && (
          <div style={{ textAlign: "center" }}>
            <br />
            {/* <h2>No Interactions. </h2>
            <h4>
              An Interval Set <InfoButton flavor="info" type={"IntervalSet"} />{" "}
              is a common way to add interactions to a Pathway and schedule them
              on an Interval. Add an Interval Set to get started!
            </h4> */}
            <AddChildInteractionButton
              setID={setID}
              parentID={parentID}
              type={"intervalSet"}
              main={false}
              numChildren={0}
              intervalSetID={null}
              shared={props.interaction.shared}
            />
            <br />
          </div>
        )}
        <br />
        {props.childInteractions}
      </div>
    );
  } else {
    // handle descendants of immediate chidren (e.g. second-level nesting)
    return (
      <div
        onClick={handleOpenIntervalSet}
        // onMouseEnter={()=>{console.log(props.node)}}
        style={{
          margin: "5px",
          padding: "10px 60px",
          backgroundColor: backgroundColor,
        }}
      >
        <Accordion
          expanded={expanded === "panel1"}
          onChange={handleChange("panel1")}
        >
          <PathwayAccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1bh-content"
            id="panel1bh-header"
            style={{
              border: "1px solid lightskyblue",
              marginBottom: 10,
              borderRadius: "5px",
              backgroundColor: "#f0f0f0",
              height: "90px",
            }}
          >
            {" "}
            <PathwayIcon />
            {props.node && props.node.condition_id !== null && (
              <div>
                <ConditionBar
                  sx={{
                    position: "relative",
                    left: -45,
                    top: -12,
                    width: "calc(100% + 56px)",
                    borderTopLeftRadius: 4,
                    borderTopRightRadius: 4,
                    marginBottom: "10px",
                  }}
                  table={InteractionNode}
                  tableID={props.node.id}
                />
              </div>
            )}
            <Box sx={{ flexGrow: 1 }}>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <h4>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                    {props.interaction.title}
                    {/* For now, we will continue to open drawer to update commset name and make it shareable */}
                    {!props.interaction.shared && (
                      <Tooltip title={"Share Interaction"}>
                        <IconButton
                          data-testid="editIntervalSetButton"
                          //disabled={props.interaction.shared}
                          onClick={(e) => {
                            e.stopPropagation();
                            // open modal, ask user to confirm share action
                            dispatch(
                              navSliceActions.openModal({
                                caresenseModalAlertType: "interaction_share",
                                caresenseModalAlertMessage: "This action will permanently make this pathway available for use on other Interactions. Proceed?",
                                caresenseModalAlertEntityID: props.interaction.id,
                                modalCallback: () => {
                                  pathway?.update("shared", true).save();
                                  if (props.node && pathway) {
                                    InteractionNode.updateInteractionSetID(props.node?.parent_interaction_id, props.node?.set_interaction_id, pathway?.id, pathway?.id)
                                  }
                                } // post callback to redux so modal alert calls it on user approval
                              }),
                            );
                          }}
                        >
                          <IosShareIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {props.interaction.shared && <Tooltip title={"Unlock shared content"}>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditMode(!editMode);
                        }}
                      >
                        {!editMode && <LockIcon style={{ color: "red" }} />}
                        {editMode && <LockOpenIcon style={{ color: "#1976d2" }} />}
                      </IconButton>
                    </Tooltip>}
                  </h4>

                </Grid>
                <Grid item xs={3}>
                  {editMode && props.interaction.shared && (
                    <span style={{ color: "red" }}>
                      <h6 style={{ color: "red", marginTop: "5px" }}>
                        <span style={{ marginTop: "-150px" }}>
                          <ErrorOutlineOutlinedIcon />
                        </span>
                        Editing shared content!
                      </h6>
                    </span>
                  )}
                </Grid>
                <Grid item xs={3}>
                  <Stack
                    justifyContent="flex-end"
                    style={{
                      position: "absolute",
                      marginTop: "10px",
                      textAlign: "right",
                    }}
                    direction="row"
                    spacing={0}
                  >
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                    {props.interaction.shared && <InteractionCopyButton
                      type="pathway"
                      interaction={props.interaction}
                      pathwayNode={pathwayNode}
                    />}

                    {props.node && (
                      <InteractionPopoutButton
                        isReferenceSet={props.isReferenceSet}
                        isInitial={props.isInitial}
                        interaction={props.interaction}
                        interactionNode={props.node}
                        parentID={props.node.parent_interaction_id}
                        setID={props.node.set_interaction_id}
                      />
                    )}
                    {props.node && !previewMode && (
                      <InteractionConditionsButton
                        disabled={!editMode}
                        interaction={props.interaction}
                        condition_id={props.node.condition_id}
                        interaction_node={props.node}
                      />
                    )}
                    {!previewMode && (
                      <InteractionEditButton
                        disabled={!editMode}
                        interaction={props.interaction}
                      />
                    )}
                    {!previewMode && (
                      <InteractionDeleteButton
                        disabled={!editMode}
                        interaction={props.interaction}
                        handler={props.handleDeleteInteraction}
                      />
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </PathwayAccordionSummary>
          <AccordionDetails>
            {props.interaction.shared && <InteractionDisabledOverlay type="pathway_node" isVisible={!editMode} />}
            {/* {isLoading && <LinearProgress/>} */}
            {/* <div>
              <AddChildInteractionButton
                setID={setID}
                parentID={parentID}
                type={props.interaction.properties.type}
                main={false}
                numChildren={props.childInteractions.length}
                intervalSetID={null}
              />
            </div> */}
            <div style={{ textAlign: "center" }}>
              <br />
              {/* <h2>No Interactions. </h2>
              <h4>
                An Interval Set{" "}
                <InfoButton flavor="info" type={"IntervalSet"} /> is a common
                way to add interactions to a Pathway and schedule them on an
                Interval. Add an Interval Set to get started!
              </h4> */}
              {isLoading && <CircularProgress />}
              {/* main one? */}
              {!isLoading && props.childInteractions.length <= 1 && (
                <AddChildInteractionButton
                  setID={setID}
                  parentID={parentID}
                  type={"intervalSet"}
                  main={false}
                  numChildren={0}
                  intervalSetID={null}
                  shared={props.interaction.shared}
                />
              )}
              {!isLoading &&
                props.childInteractions.length > 0 &&
                props.childInteractions}
            </div>
          </AccordionDetails>
        </Accordion>
      </div>
    );
  }
};
export default Pathway;
